/**
 * CancelSubscriptionSection Component
 *
 * Displays subscription cancellation status and provides cancel/reactivate actions.
 * - If active: Shows "Cancel Subscription" button with confirmation dialog
 * - If cancel_at_period_end: Shows warning banner + "Keep Subscription" button
 */

import { useState } from 'react';
import { Error as ErrorIcon, Warning } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useCancelSubscriptionMutation, useReactivateSubscriptionMutation } from '../store/api';

import { CANCEL_REASON_CATEGORIES, type CancelReasonCategoryId } from './cancelReasons';

/**
 * Request body for the cancel mutation. Defined locally (rather than importing
 * the generated `CancelSubscriptionRequest`) so the web half type-checks and
 * ships before the server half regenerates the OpenAPI schema — the deploy
 * order is web-first, server-second (GDN-2986). Structurally assignable to the
 * generated request type both before and after the server adds `categories`.
 */
interface CancelSurveyRequestBody {
  categories: CancelReasonCategoryId[];
  cancellation_reason?: string | null;
}

interface CancelSubscriptionSectionProps {
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string | number | null;
  subscriptionStatus?: string | null;
  onStatusChange?: () => void;
}

// Format date (handles both ISO string and unix timestamp)
const formatDate = (dateValue: string | number | null | undefined): string => {
  if (!dateValue) return 'N/A';
  // If it's a number (Unix timestamp), convert from seconds to milliseconds
  const date = typeof dateValue === 'number' ? new Date(dateValue * 1000) : new Date(dateValue);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export function CancelSubscriptionSection({
  cancelAtPeriodEnd,
  currentPeriodEnd,
  subscriptionStatus,
  onStatusChange,
}: CancelSubscriptionSectionProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<CancelReasonCategoryId[]>([]);
  const [cancellationReason, setCancellationReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { subscriptionManagement } = useEventTracking();

  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivating }] =
    useReactivateSubscriptionMutation();

  // Don't show section if no subscription, already canceled, or paused.
  // Paused subscriptions have no active billing period, so cancel_at_period_end
  // is not a valid Stripe operation — users should resume or let it lapse.
  if (!subscriptionStatus || subscriptionStatus === 'canceled' || subscriptionStatus === 'paused') {
    return null;
  }

  const handleCancelClick = () => {
    subscriptionManagement.cancelClicked();
    setShowCancelDialog(true);
    setError(null);
  };

  const handleCloseCancelDialog = () => {
    setShowCancelDialog(false);
    setSelectedCategories([]);
    setCancellationReason('');
    setError(null);
  };

  // Toggle a category while preserving selection order so the first-chosen
  // category is the primary reason the server maps onto Stripe's feedback enum.
  const handleToggleCategory = (categoryId: CancelReasonCategoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  const hasSelection = selectedCategories.length > 0;

  const handleConfirmCancel = async () => {
    // Client-side gate: at least one category is required before submit.
    if (!hasSelection) {
      setError('Please select at least one reason for canceling.');
      return;
    }
    setError(null);
    try {
      const requestBody: CancelSurveyRequestBody = {
        categories: selectedCategories,
        cancellation_reason: cancellationReason || null,
      };
      await cancelSubscription({
        cancelSubscriptionRequest: requestBody,
      }).unwrap();
      subscriptionManagement.cancelConfirmed({
        cancellation_reason_provided: !!cancellationReason.trim(),
        category_count: selectedCategories.length,
        primary_category: selectedCategories[0],
        categories: selectedCategories.join(','),
      });
      handleCloseCancelDialog();
      onStatusChange?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to cancel subscription. Please try again.';
      setError(errorMessage);
    }
  };

  const handleReactivate = async () => {
    setError(null);
    subscriptionManagement.reactivateClicked();
    try {
      await reactivateSubscription().unwrap();
      subscriptionManagement.reactivateConfirmed();
      onStatusChange?.();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to reactivate subscription. Please try again.';
      setError(errorMessage);
    }
  };

  // Show reactivation UI if subscription is set to cancel at period end
  if (cancelAtPeriodEnd) {
    return (
      <Box>
        <Alert
          severity="warning"
          icon={<Warning />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleReactivate}
              disabled={isReactivating}
              sx={{ fontWeight: 600 }}
            >
              {isReactivating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                'Keep Subscription'
              )}
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Your subscription is scheduled to cancel
          </Typography>
          <Typography variant="body2">
            You will lose access on {formatDate(currentPeriodEnd)}. Click &quot;Keep
            Subscription&quot; to continue your plan.
          </Typography>
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }

  // Show cancel subscription UI for active subscriptions
  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'error.main',
        bgcolor: theme => `${theme.palette.error.main}08`,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main', mb: 1 }}>
        Danger Zone
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        If you cancel, you&apos;ll continue to have access until the end of your current billing
        period ({formatDate(currentPeriodEnd)}).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="outlined"
        color="error"
        onClick={handleCancelClick}
        disabled={isCanceling}
        startIcon={isCanceling ? <CircularProgress size={16} /> : undefined}
      >
        Cancel Subscription
      </Button>

      {/* Confirmation Dialog */}
      <Dialog open={showCancelDialog} onClose={handleCloseCancelDialog} fullWidth maxWidth={"sm"}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Cancel Subscription
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Are you sure you want to cancel your subscription? You will continue to have access
            until <strong>{formatDate(currentPeriodEnd)}</strong>, after which you will lose access
            to premium features.
          </DialogContentText>
          <FormLabel
            component="legend"
            required
            sx={{ display: 'block', mb: 1, fontWeight: 600, color: 'text.primary' }}
          >
            Why are you canceling? Select all that apply.
          </FormLabel>
          <FormGroup sx={{ mb: 3 }}>
            {CANCEL_REASON_CATEGORIES.map(category => (
              <FormControlLabel
                key={category.id}
                control={
                  <Checkbox
                    checked={selectedCategories.includes(category.id)}
                    onChange={() => handleToggleCategory(category.id)}
                    disabled={isCanceling}
                  />
                }
                label={category.label}
              />
            ))}
          </FormGroup>
          <TextField
            label="Anything else? (optional)"
            multiline
            rows={3}
            fullWidth
            value={cancellationReason}
            onChange={e => setCancellationReason(e.target.value)}
            placeholder="We'd love to know more so we can improve..."
            variant="outlined"
            disabled={isCanceling}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseCancelDialog} disabled={isCanceling}>
            Keep Subscription
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={isCanceling || !hasSelection}
            startIcon={isCanceling ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isCanceling ? 'Canceling...' : 'Yes, Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
